import { AREAS, band, fmtScore } from "@/lib/heat";
import { FlowerHeatmap } from "@/components/FlowerHeatmap";

const slug = (s) => s.replace(/[^a-z0-9]+/gi, "-").toLowerCase();

const FlowerRow = ({ rows, testId }) => (
  <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3 gap-2" data-testid={`${testId}-flowers`}>
    {rows.map((r) => (
      <div key={r.label} className="rounded-lg bg-slate-50 p-2 text-center" data-testid={`${testId}-flower-${slug(r.label)}`}>
        <div className="flex justify-center">
          <FlowerHeatmap scores={r.averages} size={100} labels={false} testId={`${testId}-heatmap-${slug(r.label)}`} />
        </div>
        <div className="mt-1 text-[11px] font-medium leading-tight text-slate-800 line-clamp-2" title={r.label}>{r.label}</div>
        <div className="text-[10px] text-slate-400">{r.count} · total <span className="font-mono font-semibold text-[#1F4266]">{fmtScore(r.total)}</span></div>
      </div>
    ))}
  </div>
);

const COLUMNS = [
  { key: "gender", title: "By gender" },
  { key: "race", title: "By race / ethnicity" },
  { key: "age", title: "By age range" },
  { key: "event_group", title: "By event / client group" },
];

const BreakdownTable = ({ title, rows, testId }) => (
  <div className="avoid-break rounded-xl border border-slate-200 bg-white p-4" data-testid={testId}>
    <h3 className="font-display text-base font-semibold text-slate-900">{title}</h3>
    <FlowerRow rows={rows} testId={testId} />
    <table className="mt-3 w-full text-xs">
      <thead>
        <tr className="text-[9px] font-mono uppercase tracking-wider text-slate-400">
          <th className="py-1 text-left font-normal">Group</th>
          {AREAS.map((a) => <th key={a.key} className="py-1 text-center font-normal">{a.label.slice(0, 3)}</th>)}
          <th className="py-1 text-right font-normal">Total</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.label} className="border-t border-slate-100" data-testid={`${testId}-row-${r.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
            <td className="py-2 pr-2 text-slate-700 max-w-[130px]">
              <div className="font-medium leading-tight truncate" title={r.label}>{r.label}</div>
              <div className="text-[10px] text-slate-400">{r.count} {r.count === 1 ? "person" : "people"}</div>
            </td>
            {AREAS.map((a) => (
              <td key={a.key} className="py-2 text-center font-mono font-semibold" style={{ color: band(r.averages[a.key]).hex }}>
                {fmtScore(r.averages[a.key])}
              </td>
            ))}
            <td className="py-2 text-right font-mono font-semibold text-[#1F4266]">{fmtScore(r.total)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const DemographicSection = ({ demographics }) => {
  if (!demographics) return null;
  return (
    <section className="page-break mt-12" data-testid="group-demographics">
      <h2 className="text-2xl font-bold tracking-tight text-slate-900">Demographic breakdown</h2>
      <p className="mt-1 text-sm text-slate-500">
        Average score per area (out of 36) for each group, side by side. Colours follow the level of each score. Someone who chose more than one gender, race/ethnicity or event/client group is counted in each.
      </p>
      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        {COLUMNS.map((c) => (
          <BreakdownTable key={c.key} title={c.title} rows={demographics[c.key] || []} testId={`demo-${c.key}`} />
        ))}
      </div>
    </section>
  );
};
